import { useEffect, useState } from 'react';

import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Switch, TextField } from '@mui/material';

import { useApiClient, useToastService } from '@@common';
import { DialogHeaderActions } from '@@components';

import { AdminClient, TenantModel } from '../../../api/api';

interface EditTenantDialogProps {
	open: boolean;
	setOpen: (open: boolean) => void;
	tenant: TenantModel;
	onSuccess: () => void;
}

export default function EditTenantDialog({ open, setOpen, tenant, onSuccess }: EditTenantDialogProps) {
	const client = useApiClient(AdminClient);
	const toastService = useToastService();

	const [code, setCode] = useState(tenant.code);
	const [name, setName] = useState(tenant.name || '');
	const [description, setDescription] = useState(tenant.description || '');
	const [isActive, setIsActive] = useState(tenant.isActive);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isMaximized, setIsMaximized] = useState(false);

	useEffect(() => {
		if (open) {
			setCode(tenant.code);
			setName(tenant.name || '');
			setDescription(tenant.description || '');
			setIsActive(tenant.isActive);
			setIsSubmitting(false);
			setIsMaximized(false);
		}
	}, [open, tenant.code, tenant.name, tenant.description, tenant.isActive]);

	const handleUpdate = async () => {
		if (!code.trim()) {
			toastService.error('Code is required');
			return;
		}

		setIsSubmitting(true);
		try {
			await client.updateTenant(tenant.id, {
				code: code.trim(),
				name: name.trim() || undefined,
				description: description.trim() || undefined,
				isActive,
			});
			toastService.success(`Tenant "${code.trim()}" updated successfully`);
			setOpen(false);
			onSuccess();
		} catch (error) {
			console.error('Error updating tenant:', error);
			const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
			toastService.error(`Failed to update tenant: ${errorMessage}`);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleKeyPress = (event: React.KeyboardEvent) => {
		if (event.key === 'Enter' && !isSubmitting) {
			event.preventDefault();
			handleUpdate();
		}
	};

	return (
		<Dialog open={open} onClose={() => !isSubmitting && setOpen(false)} maxWidth="sm" fullWidth fullScreen={isMaximized}>
			<DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
				<span style={{ flex: 1 }}>Edit Tenant</span>
				<DialogHeaderActions
					isMaximized={isMaximized}
					onToggleMaximized={() => setIsMaximized((value) => !value)}
					onClose={() => setOpen(false)}
					closeDisabled={isSubmitting}
				/>
			</DialogTitle>
			<DialogContent>
				<TextField
					autoFocus
					margin="dense"
					label="Code"
					type="text"
					fullWidth
					variant="outlined"
					value={code}
					onChange={(e) => setCode(e.target.value)}
					onKeyPress={handleKeyPress}
					disabled={isSubmitting}
					required
					helperText="Unique identifier for the tenant"
				/>
				<TextField
					margin="dense"
					label="Name"
					type="text"
					fullWidth
					variant="outlined"
					value={name}
					onChange={(e) => setName(e.target.value)}
					onKeyPress={handleKeyPress}
					disabled={isSubmitting}
					helperText="Display name for the tenant"
				/>
				<TextField
					margin="dense"
					label="Description"
					type="text"
					fullWidth
					variant="outlined"
					value={description}
					onChange={(e) => setDescription(e.target.value)}
					onKeyPress={handleKeyPress}
					disabled={isSubmitting}
					multiline
					rows={3}
					helperText="Optional description for the tenant"
				/>
				<FormControlLabel
					control={<Switch checked={isActive} onChange={(event) => setIsActive(event.target.checked)} disabled={isSubmitting} />}
					label="Active"
					sx={{ mt: 1 }}
				/>
			</DialogContent>
			<DialogActions>
				<Button onClick={() => setOpen(false)} disabled={isSubmitting}>
					Cancel
				</Button>
				<Button onClick={handleUpdate} variant="contained" disabled={isSubmitting || !code.trim()}>
					{isSubmitting ? 'Updating...' : 'Update'}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
